import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ArrowLeft } from "react-feather";
import { Page, PageHeader, Card, Button, Field, TextInput, Select, Badge } from "../../../../../ui";
import SegmentedControl from "../ui-pending/SegmentedControl";
import StatTile from "../ui-pending/StatTile";
import "../ui-pending/ui-pending.scss";
import { toast } from "react-toastify";
import ldQaFormData from "./ldqaform.json";
import { addNewQaList, courseLevel, getGraded } from "../../../../../Api/apiUtils";
import { trackEvent, MixpanelEvents } from "../../../../../utils/mixpanelTracking";
import RichTextEditor from "./RichTextEditor";
import { isRichTextEmpty } from "./richTextUtils";
import "./qareports.scss";

type LdQuestion = {
  id: string;
  title: string;
  type: string;
  max_marks?: number;
  options?: { marks?: number[]; breakup?: string };
};

type LdSection = {
  section_title: string;
  section_total?: number;
  questions?: LdQuestion[];
};

const formData = ldQaFormData;
const PAGE_FIELDS = formData.pages[0]?.fields || [];
const MARK_SECTIONS = (formData.pages[1]?.sections || []) as LdSection[];
const MAX_SCORE = formData.maxScore || 50;

const LdQaReports: React.FC = () => {
  const [searchParams] = useSearchParams();
  const classId = searchParams.get("classId");
  const [currentPage, setCurrentPage] = useState(1);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const auditorsList = formData.auditors || [];
  const auditCategoryList = formData.auditCategories || [];
  const totalPages = formData.pages.length;

  useEffect(() => {
    const loadFormData = async () => {
      if (!classId) return;
      try {
        const storedEventDetails = localStorage.getItem(`qaEventDetails_${classId}`);
        if (!storedEventDetails) return;
        const eventDetails = JSON.parse(storedEventDetails);
        const autoPopulatedValues: Record<string, string> = {};

        if (eventDetails.teacherName) autoPopulatedValues.Teacher = eventDetails.teacherName;
        if (eventDetails.studentName) autoPopulatedValues.Student = eventDetails.studentName;
        if (eventDetails.slotDate) autoPopulatedValues["Date of Class"] = eventDetails.slotDate;
        if (eventDetails.classNumber) autoPopulatedValues["Class Number"] = String(eventDetails.classNumber);
        if (eventDetails.lessonNumber) autoPopulatedValues["Lesson Number"] = String(eventDetails.lessonNumber);
        if (eventDetails.ageGroup) autoPopulatedValues.Age = eventDetails.ageGroup;
        if (eventDetails.studentTimezone) autoPopulatedValues.Timezone = eventDetails.studentTimezone;
        autoPopulatedValues["Date of Audit"] = new Date().toISOString().split("T")[0];

        if (eventDetails.studentId && eventDetails.courseId) {
          try {
            const response = await getGraded(eventDetails.studentId, eventDetails.courseId, "getCertification");
            autoPopulatedValues["Graded Certification"] = response?.gradedCertification || "No certificate";
            autoPopulatedValues.Course = response?.courseName || eventDetails.courseName || "";
          } catch {
            autoPopulatedValues["Graded Certification"] = "No certificate";
            if (eventDetails.courseName) autoPopulatedValues.Course = eventDetails.courseName;
          }
        } else {
          autoPopulatedValues["Graded Certification"] = "No certificate";
          if (eventDetails.courseName) autoPopulatedValues.Course = eventDetails.courseName;
        }

        if (eventDetails.studentUserId && eventDetails.courseId) {
          try {
            const levelResponse = await courseLevel(eventDetails.studentUserId, eventDetails.courseId);
            autoPopulatedValues.Level =
              levelResponse?.status?.[0]?.level_name || eventDetails.level || "";
          } catch {
            if (eventDetails.level) autoPopulatedValues.Level = eventDetails.level;
          }
        } else if (eventDetails.level) {
          autoPopulatedValues.Level = eventDetails.level;
        }

        setFormValues(autoPopulatedValues);
        setAutoPopulatedFields(new Set(Object.keys(autoPopulatedValues)));
        trackEvent(MixpanelEvents.QA_REPORT_PAGE_VIEW, {
          qaType: "L&D",
          classId,
          teacherUserId: eventDetails.teacherUserId,
          studentUserId: eventDetails.studentUserId,
          studentName: eventDetails.studentName,
          teacherName: eventDetails.teacherName,
        });
      } catch (error) {
        console.error("Error loading L&D QA details:", error);
      }
    };
    void loadFormData();
  }, [classId]);

  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const validateCurrentPage = () => {
    const errors: Record<string, string> = {};
    if (currentPage === 1) {
      PAGE_FIELDS.forEach((field) => {
        if (!formValues[field]) errors[field] = `${field} is required`;
      });
    } else {
      MARK_SECTIONS.forEach((section) => {
        section.questions?.forEach((question) => {
          if (!formValues[question.id]) errors[question.id] = `${question.title} is required`;
        });
      });
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentPage()) {
      toast.error("Please fill all required fields before proceeding");
      window.scrollTo(0, 0);
      return;
    }
    setCurrentPage((page) => Math.min(page + 1, totalPages));
    window.scrollTo(0, 0);
  };

  const grandTotal = MARK_SECTIONS.flatMap((section) => section.questions || []).reduce((sum, question) => {
    const n = parseInt(formValues[question.id], 10);
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);

  const toFiniteNumber = (value: unknown): number | undefined => {
    if (value === null || value === undefined || value === "") return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  };

  const buildLdTeacherQaPayload = () => {
    if (!classId) throw new Error("Class ID is required");
    const stored = localStorage.getItem(`qaEventDetails_${classId}`);
    if (!stored) throw new Error("Event details not found");
    const eventDetails = JSON.parse(stored);
    const mark = (id: string) => formValues[id] || "";
    // teacherQAScore looks up teacher profile id. Do not send teacherUserId.
    const teacherId =
      toFiniteNumber(eventDetails.teacherId) ??
      toFiniteNumber(localStorage.getItem("SelectedCourseTeacherId"));
    if (teacherId == null) {
      throw new Error("Teacher ID is required");
    }
    const numericClassId = toFiniteNumber(classId) ?? classId;
    const sessionDateRaw = String(formValues["Date of Class"] || eventDetails.slotDate || "");
    const sessionDate = sessionDateRaw.split("T")[0].split(" ")[0];

    return {
      teacherName: formValues.Teacher || eventDetails.teacherName || "",
      teacherCategory: formValues["Audit Category"] || "",
      studentName: formValues.Student || eventDetails.studentName || "",
      sessionDate,
      course: formValues.Course || eventDetails.courseName || "",
      ageGroup: formValues.Age || eventDetails.ageGroup || "",
      auditDate: formValues["Date of Audit"] || "",
      feedbackStatus: formValues["Audit Category"] || "",
      feedbackLink: "",
      observation: isRichTextEmpty(formValues.comment) ? "" : formValues.comment || "",
      teacherId,
      classId: numericClassId,
      classNumber: formValues["Class Number"] || eventDetails.classNumber || "",
      lessonNumber: formValues["Lesson Number"] || eventDetails.lessonNumber || "",
      auditor: formValues.Auditor || "",
      timezone: formValues.Timezone || eventDetails.studentTimezone || "",
      level: formValues.Level || eventDetails.level || "",
      gradedCertification: formValues["Graded Certification"] || "",
      qaType: "L&D",
      qaScore: grandTotal,
      qaPercentage: MAX_SCORE ? Math.round((grandTotal / MAX_SCORE) * 100) : 0,
      payload: [
        {
          ProfessionalAppearanceAndGrooming: {
            artiumJacket: mark("artium_jacket"),
            overallProfessionalPresentation: mark("overall_professional_presentation"),
          },
          CameraFramingAndVisualSetup: {
            videoOnCameraAngleFramingVisibility: mark("video_camera_framing"),
            lightingAndBackgroundEnvironment: mark("lighting_background"),
          },
          InternetAndTechnicalStability: {
            internetStability: mark("internet_stability"),
          },
          ClassDurationAndPunctuality: {
            joiningOnTime: mark("joining_on_time"),
            classDuration: mark("class_duration"),
          },
          InterruptionsAndBreaks: {
            noMultitaskingUsageOfPhoneTalksEatingDistracted: mark("no_multitasking_distracted"),
            noBreaksInterruptions: mark("no_breaks_interruptions"),
          },
          ProfessionalCommunicationAndConduct: {
            respectfulPositiveProfessionalCommunication: mark("respectful_communication"),
            choiceOfLanguageWords: mark("choice_of_language"),
            noRudenessOrFrustration: mark("no_rudeness_frustration"),
          },
          BodyLanguageAndOnCameraPresence: {
            postureEyeContactFacialExpressionsPositiveBodyLanguage: mark("posture_eye_contact"),
            energyAndEnthusiasm: mark("energy_enthusiasm"),
          },
          Closing: {
            doubtSolving: mark("doubt_solving"),
            nextClassBooking: mark("next_class_booking"),
          },
        },
      ],
    };
  };

  const handleSubmit = () => {
    if (!validateCurrentPage()) {
      toast.error("Please fill all required fields before submitting");
      window.scrollTo(0, 0);
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);
    try {
      const payload = buildLdTeacherQaPayload();
      const response = await addNewQaList(payload);
      if (response.status === 200 || response.status === 201) {
        toast.success("L&D QA submitted successfully!");
        trackEvent(MixpanelEvents.QA_ADMIN_AUDIT_CREATED, {
          source: "ld_qa_analysis",
          qaType: "L&D",
          classId,
          teacherId: payload.teacherId != null ? String(payload.teacherId) : undefined,
          qaScore: payload.qaScore,
          qaPercentage: payload.qaPercentage,
          auditor: payload.auditor,
        });
        setTimeout(() => window.close(), 1000);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to submit L&D QA";
      toast.error(errorMessage);
      trackEvent(MixpanelEvents.QA_ADMIN_AUDIT_CREATE_FAILED, {
        source: "ld_qa_analysis",
        qaType: "L&D",
        classId,
        errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page className="tp-qa tp-qa--ld">
      <PageHeader
        eyebrow="L&D QA"
        title={currentPage === 1 ? "L&D QA Analysis" : "L&D Parameters & Marks"}
        actions={
          currentPage > 1 ? (
            <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden />} onClick={() => setCurrentPage(1)}>
              Back
            </Button>
          ) : undefined
        }
      />

      {currentPage === 1 && (
        <Card title="Class details">
          <div className="tp-qa__grid">
            {PAGE_FIELDS.map((field) => (
              <Field key={field} label={field} required error={validationErrors[field]}>
                {(control) =>
                  field === "Auditor" ? (
                    <Select
                      {...control}
                      value={formValues[field] || ""}
                      onChange={(e) => handleInputChange(field, e.target.value)}
                      options={[
                        { value: "", label: "Select Auditor" },
                        ...auditorsList.map((auditor) => ({ value: auditor, label: auditor })),
                      ]}
                    />
                  ) : field === "Audit Category" ? (
                    <Select
                      {...control}
                      value={formValues[field] || ""}
                      onChange={(e) => handleInputChange(field, e.target.value)}
                      options={[
                        { value: "", label: "Select Audit Category" },
                        ...auditCategoryList.map((category) => ({ value: category, label: category })),
                      ]}
                    />
                  ) : (
                    <TextInput
                      {...control}
                      type="text"
                      value={formValues[field] || ""}
                      onChange={(e) => {
                        if (autoPopulatedFields.has(field)) return;
                        handleInputChange(field, e.target.value);
                      }}
                      readOnly={autoPopulatedFields.has(field)}
                    />
                  )
                }
              </Field>
            ))}
          </div>
        </Card>
      )}

      {currentPage === 2 && (
        <>
          <div className="tp-qa__sections">
            {MARK_SECTIONS.map((section) => (
              <Card
                key={section.section_title}
                title={section.section_title}
                actions={
                  section.section_total != null ? <Badge tone="neutral">Total {section.section_total}</Badge> : undefined
                }
              >
                {section.questions?.map((question) => (
                  <div key={question.id} className="tp-qa__question">
                    <h3 className="tp-qa__question-title">
                      {question.title}
                      <span className="tp-qa__required" aria-hidden>*</span>
                    </h3>
                    <div className="tp-qa__marks">
                      <SegmentedControl
                        label={`${question.title} marks`}
                        size="sm"
                        value={formValues[question.id] ?? ""}
                        options={(question.options?.marks || []).map((score) => ({
                          value: String(score),
                          label: score,
                        }))}
                        onChange={(score) => handleInputChange(question.id, score)}
                      />
                      {question.options?.breakup ? <p className="tp-qa__hint">{question.options.breakup}</p> : null}
                      {validationErrors[question.id] && (
                        <span className="tp-qa__error" role="alert">{validationErrors[question.id]}</span>
                      )}
                    </div>
                  </div>
                ))}
              </Card>
            ))}
          </div>
          <Card>
            <StatTile label="Grand Total" value={`${grandTotal} / ${MAX_SCORE}`} tone="primary" />
            <div className="tp-qa__comment">
              <Field label="Comment">
                {() => (
                  <RichTextEditor
                    value={formValues.comment || ""}
                    onChange={(html) => handleInputChange("comment", html)}
                    placeholder="Enter comments observed during the session"
                  />
                )}
              </Field>
            </div>
          </Card>
        </>
      )}

      <div className="tp-qa__footer">
        <div className="tp-qa__footer-side">
          {currentPage > 1 && (
            <Button variant="secondary" onClick={() => setCurrentPage(1)}>
              Previous
            </Button>
          )}
        </div>
        <div className="tp-qa__footer-side">
          <span className="tp-qa__page-indicator">
            Page {currentPage} of {totalPages}
          </span>
          {currentPage < totalPages && (
            <Button variant="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentPage === totalPages && (
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit L&D QA"}
            </Button>
          )}
        </div>
      </div>

      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
        <ModalHeader toggle={() => setShowConfirmModal(false)}>Confirm Submission</ModalHeader>
        <ModalBody>
          Are you sure you want to submit this L&D QA report? Once submitted, the report will be saved and this tab will be closed.
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void confirmSubmit()} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Confirm & Submit"}
          </Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
};

export default LdQaReports;
